import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Cancel01Icon,
  Copy01Icon,
  Tick02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import addressApi from "api/landlord/address";
import {
  CustomButtonLinkScope,
  CustomButtonLinkTarget,
  CustomButtonLinkTargetsResponse
} from "types/landlord/address";
import {
  Button,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  InputSize,
  Modal
} from "shared/components";
import { BaseInput } from "shared/components/forms/BaseInput";
import {
  CopyStatus,
  useCopyToClipboard
} from "shared/hooks/use-copy-to-clipboard";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import {
  isRunActive,
  useBulkBrandingProgress
} from "providers/BulkBrandingProgress";
import { useBulkCustomButtonLinkProgress } from "providers/BulkCustomButtonLinkProgress";
import { CustomButtonLinkTargetsList } from "./CustomButtonLinkTargetsList";
import { OverwriteCustomButtonLinkDialog } from "./OverwriteCustomButtonLinkDialog";
import {
  CUSTOM_BUTTON_LINK_TARGETS_PER_PAGE,
  getApplyErrorMessage,
  MAX_CUSTOM_BUTTON_LINK_TARGETS,
  targetLabel
} from "./helpers";

const SEARCH_DEBOUNCE_MS = 500;
const COPIED_FEEDBACK_MS = 1000;

const LinkToApplyPreview = ({ link }: { link: string }) => {
  const { status, copy, reset } = useCopyToClipboard();
  const copied = status === CopyStatus.Copied;

  useEffect(() => {
    if (!copied) return;

    const timer = setTimeout(reset, COPIED_FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [copied, reset]);

  return (
    <div className="flex items-center gap-3 rounded-lg bg-background-900 px-4 py-3">
      <button
        aria-label={copied ? "Copied" : "Copy link"}
        className="shrink-0 rounded text-primary-500 transition-colors hover:text-primary-600 focus:outline-1 focus:outline-primary-500"
        onClick={() => copy(link)}
        type="button"
      >
        <span
          className="animate-bounce-in block"
          key={copied ? "tick" : "copy"}
        >
          <HugeiconsIcon
            className={copied ? "text-success-500" : undefined}
            icon={copied ? Tick02Icon : Copy01Icon}
            size={18}
            strokeWidth={2}
          />
        </span>
      </button>
      <p
        className="min-w-0 flex-1 truncate text-sm font-medium text-content-1000"
        title={link}
      >
        {link}
      </p>
    </div>
  );
};

export const ApplyCustomButtonLinkModal = ({
  opened,
  onClose,
  scope,
  sourceId,
  sourceName,
  link
}: {
  opened: boolean;
  onClose: () => void;
  scope: CustomButtonLinkScope;
  sourceId: number;
  sourceName: string;
  link: string;
}) => {
  const dictionary = useUnitsPropertiesDictionary();
  const {
    startTracking,
    run: activeRun,
    status: activeRunStatus
  } = useBulkCustomButtonLinkProgress();
  const { run: activeBrandingRun, status: activeBrandingRunStatus } =
    useBulkBrandingProgress();
  const isBlockedByActiveRun =
    isRunActive(activeRun, activeRunStatus) ||
    isRunActive(activeBrandingRun, activeBrandingRunStatus);
  const [selected, setSelected] = useState<Map<number, CustomButtonLinkTarget>>(
    () => new Map()
  );
  const [applyMode, setApplyMode] = useState<"explicit" | "all">("explicit");
  const [excludedIds, setExcludedIds] = useState<Set<number>>(() => new Set());
  const [search, setSearch] = useState("");
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const [debouncedSearch] = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  const [previousSearch, setPreviousSearch] = useState(debouncedSearch);
  if (previousSearch !== debouncedSearch) {
    setPreviousSearch(debouncedSearch);
    if (applyMode === "all") {
      setApplyMode("explicit");
      setExcludedIds(new Set());
    }
  }

  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery<CustomButtonLinkTargetsResponse>({
    queryKey: [
      scope === "property" ? "properties" : "units",
      String(sourceId),
      "custom_button_link_targets",
      debouncedSearch,
      CUSTOM_BUTTON_LINK_TARGETS_PER_PAGE
    ],
    queryFn: ({ pageParam }) =>
      addressApi.getCustomButtonLinkTargets({
        scope,
        sourceId,
        page: Number(pageParam),
        per_page: CUSTOM_BUTTON_LINK_TARGETS_PER_PAGE,
        search: debouncedSearch.trim() || undefined
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.current_page + 1 : undefined,
    enabled: opened
  });

  const targets = useMemo<CustomButtonLinkTarget[]>(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data]
  );

  const targetById = useMemo(
    () => new Map(targets.map((target) => [target.id, target])),
    [targets]
  );

  const selectedFresh = useMemo(
    () =>
      Array.from(selected.values()).map(
        (target) => targetById.get(target.id) ?? target
      ),
    [selected, targetById]
  );
  const overwriteTargets = useMemo(
    () =>
      selectedFresh.filter(
        (target) => target.has_existing_link && !target.pms_synced
      ),
    [selectedFresh]
  );
  const pmsSyncedTargets = useMemo(
    () => selectedFresh.filter((target) => target.pms_synced),
    [selectedFresh]
  );

  const totalMatchingCount = data?.pages[0]?.total_count ?? 0;
  const allModeOverwriteCount = data?.pages[0]?.extra?.existing_link_count ?? 0;
  const allModePmsSyncedCount = data?.pages[0]?.extra?.pms_synced_count ?? 0;
  const isAllMode = applyMode === "all";
  const checkedIds = useMemo(() => {
    if (!isAllMode) return selected;

    const map = new Map<number, CustomButtonLinkTarget>();
    targets.forEach((target) => {
      if (!excludedIds.has(target.id)) map.set(target.id, target);
    });
    return map;
  }, [isAllMode, selected, targets, excludedIds]);

  const selectedCount = isAllMode
    ? Math.max(totalMatchingCount - excludedIds.size, 0)
    : selected.size;
  const isOverCap =
    !isAllMode && selected.size > MAX_CUSTOM_BUTTON_LINK_TARGETS;

  const closeAndReset = () => {
    setSelected(new Map());
    setApplyMode("explicit");
    setExcludedIds(new Set());
    setSearch("");
    setIsConfirmOpen(false);
    onClose();
  };

  const { mutate: applyLink, isPending: isEnqueuing } = useMutation({
    mutationKey: ["BULK", "APPLY", "CUSTOM_BUTTON_LINK"],
    mutationFn: (
      payload:
        | { target_ids: number[] }
        | { apply_to_all: true; search?: string; excluded_ids?: number[] }
    ) =>
      addressApi.applyCustomButtonLink({
        scope,
        sourceId,
        ...payload
      }),
    onSuccess: (response) => {
      startTracking({ scope, sourceId, bulkApplyId: response.id });
      closeAndReset();
    },
    onError: (mutationError) => {
      setIsConfirmOpen(false);
      toast.error(
        `Failed to apply the link: ${getApplyErrorMessage(mutationError, scope, dictionary)}`
      );
    }
  });

  const handleClose = () => {
    if (isEnqueuing) return;

    closeAndReset();
  };

  const handleConfirmClose = () => {
    if (isEnqueuing) return;

    setIsConfirmOpen(false);
  };

  const toggleTarget = (target: CustomButtonLinkTarget) => {
    if (isAllMode) {
      setExcludedIds((prev) => {
        const next = new Set(prev);
        if (next.has(target.id)) next.delete(target.id);
        else next.add(target.id);

        return next;
      });
      return;
    }

    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(target.id)) next.delete(target.id);
      else next.set(target.id, target);

      return next;
    });
  };

  const handleSelectAllClick = () => {
    setSelected(new Map());
    setExcludedIds(new Set());
    setApplyMode(isAllMode ? "explicit" : "all");
  };

  const runApply = () => {
    if (isAllMode) {
      applyLink({
        apply_to_all: true,
        search: debouncedSearch.trim() || undefined,
        excluded_ids: Array.from(excludedIds)
      });
      return;
    }

    applyLink({ target_ids: Array.from(selected.keys()) });
  };

  const needsConfirmation = isAllMode
    ? allModeOverwriteCount > 0 || allModePmsSyncedCount > 0
    : overwriteTargets.length > 0 || pmsSyncedTargets.length > 0;

  const handleApplyClick = () => {
    if (needsConfirmation) {
      setIsConfirmOpen(true);
      return;
    }

    runApply();
  };

  const targetsPlural = targetLabel(2, scope, dictionary);

  return (
    <>
      <Modal
        onClose={handleClose}
        opened={opened && !isConfirmOpen}
        panelClassName="flex max-h-[90vh] w-full max-w-[720px] flex-col overflow-hidden"
      >
        <header className="shrink-0 border-b border-alpha-40 p-6">
          <div className="flex items-start justify-between gap-4">
            <h3 className="text-xl font-bold text-content-1000">
              Apply custom button link to multiple {targetsPlural}
            </h3>
            <IconButton
              HugeIcon={Cancel01Icon}
              disabled={isEnqueuing}
              onClick={handleClose}
              size={IconButtonSize.LG}
              variant={IconButtonVariant.TEXT_BASE}
            />
          </div>
          <p className="mt-2 text-sm text-content-400">
            The custom button link saved on {sourceName} will be applied to the{" "}
            {targetsPlural} you select below.
          </p>
        </header>

        <div className="shrink-0 space-y-2 border-b border-alpha-40 px-6 py-4">
          <p className="text-xs font-medium text-content-400">Link to apply</p>
          <LinkToApplyPreview link={link} />
        </div>

        <div className="flex min-h-0 flex-1 flex-col space-y-2 px-6 py-4">
          <p className="shrink-0 text-xs font-medium text-content-400">
            {scope === "property" ? dictionary.Properties : dictionary.Units}
          </p>

          <div className="flex shrink-0 items-center justify-between gap-4">
            <BaseInput
              className="w-full max-w-[293px]"
              id="custom-button-link-target-search"
              onChange={setSearch}
              placeholder="Search"
              size={InputSize.SM}
              type="search"
              value={search}
            />
            <Button
              className="shrink-0"
              disabled={totalMatchingCount === 0}
              onClick={handleSelectAllClick}
              variant={ButtonVariant.TEXT}
            >
              {isAllMode ? "Deselect all" : "Select all"}
            </Button>
          </div>

          {isAllMode ? (
            <p className="shrink-0 text-xs text-content-300">
              Applying to every {targetLabel(1, scope, dictionary)} matching
              your search — uncheck any you want to skip. This overwrites
              existing links on affected {targetsPlural}.
            </p>
          ) : null}

          <div className="-mx-3 !mt-6 flex min-h-0 flex-1 flex-col">
            <CustomButtonLinkTargetsList
              error={error}
              hasNextPage={hasNextPage}
              hasSearch={Boolean(debouncedSearch.trim())}
              isError={isError}
              isFetchingNextPage={isFetchingNextPage}
              isLoading={isLoading}
              onLoadMore={fetchNextPage}
              onToggle={toggleTarget}
              scope={scope}
              selectedIds={checkedIds}
              visibleTargets={targets}
            />
          </div>
        </div>

        <footer className="flex shrink-0 items-center justify-between gap-4 border-t border-alpha-40 px-6 py-4">
          <div className="min-w-0 space-y-1">
            <p className="text-sm text-content-400">
              {selectedCount} {targetLabel(selectedCount, scope, dictionary)}{" "}
              selected
            </p>
            {isOverCap ? (
              <p className="text-sm font-medium text-danger-500">
                You can apply to at most {MAX_CUSTOM_BUTTON_LINK_TARGETS}{" "}
                {targetsPlural} at a time.
              </p>
            ) : null}
            {isBlockedByActiveRun ? (
              <p className="text-sm font-medium text-warning-600">
                A previous bulk apply run is still in progress — wait for it to
                finish or cancel it (top-right) before starting another.
              </p>
            ) : null}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <Button disabled={isEnqueuing} onClick={handleClose}>
              Cancel
            </Button>
            <Button
              disabled={
                selectedCount === 0 || isOverCap || isBlockedByActiveRun
              }
              loading={isEnqueuing}
              onClick={handleApplyClick}
              variant={ButtonVariant.PRIMARY}
            >
              Apply link
            </Button>
          </div>
        </footer>
      </Modal>

      <OverwriteCustomButtonLinkDialog
        allModeOverwriteCount={allModeOverwriteCount}
        allModePmsSyncedCount={allModePmsSyncedCount}
        canClose={!isEnqueuing}
        isAllMode={isAllMode}
        isPending={isEnqueuing}
        onClose={handleConfirmClose}
        onConfirm={runApply}
        opened={isConfirmOpen}
        overwriteTargets={overwriteTargets}
        pmsSyncedTargets={pmsSyncedTargets}
        scope={scope}
        selectedCount={selectedCount}
        sourceName={sourceName}
      />
    </>
  );
};
